import { Label, Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import { Apps24Regular, DocumentText24Regular, People24Regular } from "@fluentui/react-icons";
import { Divider, Paper } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import star from "../../../assets/images-homepage/starr.png";
import { TopCustomerProps, TopProductProps, TopReportProps } from "../../../pages/HomePage";
import {styles } from "./FavoritesSection.styles";

const showShimmerLabel = (
  <div className={styles.shimmerLabelItem}>
    <div style={{ width: "50%" }}>
      <Shimmer
        shimmerElements={[{ type: ShimmerElementType.line, height: 20 }]}
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{ shimmerWrapper: { borderRadius: 20 } }}
      />
    </div>
    <Shimmer
      shimmerElements={[
        { type: ShimmerElementType.circle, height: 20 }
      ]}
      shimmerColors={{ shimmer: "var(--shimmer-color)" }}
    />
  </div>
);

const shimmerLine = (
  <>
    <Shimmer
      shimmerElements={[
        { type: ShimmerElementType.circle, height: 20 },
      ]}
      shimmerColors={{ shimmer: "var(--shimmer-color)" }}
    />
    <div style={{ flex: 1 }}>
      <Shimmer
        shimmerElements={[{ type: ShimmerElementType.line, height: 20 }]}
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{ shimmerWrapper: { borderRadius: 20 } }}
      />
    </div>
  </>
);

const shimmerLoader = (
  <Stack
    styles={{ root: { width: "calc(100% - 10px)", boxSizing: "border-box" } }}
    tokens={{ padding: 10, childrenGap: 10 }}
    wrap
  >
    {Array.from({ length: 5 }).map((_, idx) => (
      <div key={`loading-${idx + 1}`} className={styles.shimmerLoaderItem}>
        {shimmerLine}
      </div>
    ))}
  </Stack>
);

type FavoritesData = {
  customers: TopCustomerProps[];
  customerTitle: string;
  isFetching: boolean;
  products: TopProductProps[];
  productTitle: string;
  reports: TopReportProps[];
  reportTitle: string;
};

type FavoritesListProps = {
  icon: React.ElementType;
  idKey: string;
  isFetching: boolean;
  items: TopCustomerProps[] | TopProductProps[] | TopReportProps[];
  labelKey: string;
  onItemClick: (name: string, id: string) => () => void;
};

const FavoritesList = ({ icon: Icon, idKey, isFetching, items, labelKey, onItemClick }: FavoritesListProps) => {
  if (isFetching) {
    return <>{shimmerLoader}</>;
  }

  return (
    <Stack
      styles={{ root: { width: "calc(100% - 10px)", boxSizing: "border-box" } }}
      tokens={{ padding: 10, childrenGap: 10 }}
      wrap
    >
      {items.map((item: any) => (
        <Stack
          className={styles.labelStyles}
          horizontal
          key={item[idKey]}
          onClick={onItemClick(item[labelKey], item[idKey])}
          styles={{ root: { width: "100%", boxSizing: "border-box" } }}
          tokens={{ childrenGap: 10 }}
          wrap
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon className={styles.iconStyle} />
            <Stack verticalAlign="center" tokens={{ padding: 2, childrenGap: 2 }} wrap>
              <Label className={styles.commonLabel}>{item[labelKey]}</Label>
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

const FavoritesSection = ({ customers, customerTitle, isFetching, products, productTitle, reports, reportTitle }: FavoritesData) => {
  const navigate = useNavigate();

  const handleCustomerItemClick = (tenantName: string, tenantId: string) => () => {
    navigate(`/customer?id=${tenantId}`);
  };

  const handleReportClick = (reportName: string, reportId: string) => () => {
    navigate(`/reportview/reportid=${reportId}`, {
      state: { reportName: reportName }
    });
  };

  const handleProductItemClick = (productName: string, productId: string) => () => {
    navigate(`/productview?productid=${productId}&productname=${productName}`);
  };

  return (
    <Stack horizontal tokens={{ padding: 10, childrenGap: 15 }} wrap>
      <Paper className={styles.paperStyle}>
        {isFetching ? (
          showShimmerLabel
        ) : (
          <Stack horizontal className={styles.headerContainer}>
            <Label className={styles.labelStyle}>{customerTitle}</Label>
            <img src={star} className={styles.imageStyle} alt="Star icon" />
          </Stack>
        )}
        <Divider variant="middle" className={styles.dividerStyle} />
        <FavoritesList
          icon={People24Regular}
          idKey="tenantId"
          isFetching={isFetching}
          items={customers}
          labelKey="tenantName"
          onItemClick={handleCustomerItemClick}
        />
      </Paper>
      <Paper className={styles.paperStyle}>
        {isFetching ? (
          showShimmerLabel
        ) : (
          <Stack horizontal className={styles.headerContainer}>
            <Label className={styles.labelStyle}>{productTitle}</Label>
            <img src={star} className={styles.imageStyle} alt="Star icon" />
          </Stack>
        )}
        <Divider variant="middle" className={styles.dividerStyle} />
        <FavoritesList
          icon={Apps24Regular}
          idKey="productId"
          isFetching={isFetching}
          items={products}
          labelKey="productName"
          onItemClick={handleProductItemClick}
        />
      </Paper>
      <Paper className={styles.paperStyle}>
        {isFetching ? (
          showShimmerLabel
        ) : (
          <Stack horizontal className={styles.headerContainer}>
            <Label className={styles.labelStyle}>{reportTitle}</Label>
            <img src={star} className={styles.imageStyle} alt="Star icon" />
          </Stack>
        )}
        <Divider variant="middle" className={styles.dividerStyle} />
        <FavoritesList
          icon={DocumentText24Regular}
          idKey="reportId"
          isFetching={isFetching}
          items={reports}
          labelKey="report"
          onItemClick={handleReportClick}
        />
      </Paper>
    </Stack>
  );
};

export default FavoritesSection;
